// src/pages/Interviews.js
import { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import Navbar from "../components/Navbar";
import { AuthContext } from "../contexts/AuthContext";
import {
  apiFetch,
  post as apiPost,
  put as apiPut,
  fetchOrganizations,
  getSelectedOrgId,
  setSelectedOrgId,
} from "../services/api";
import { useLocation } from "react-router-dom";
import { interviewStatusLabel } from "../utils/badges";

/* ---------------- Component ---------------- */
export default function Interviews() {
  const { user } = useContext(AuthContext);
  const isSuper = (user?.group_role || "").toLowerCase() === "superadmin";
  const location = useLocation();
  const preselectApplied = useRef(false);

  // ==================== DEFAULTS (must be defined BEFORE use) ====================
  // Default language: us-en (American English)
  // Default voice: Kore (INTERVIEW_VOICE.interview_voice_id = 12)
  // IMPORTANT: These attributes (gender/style/pitch) must match your DB row for Kore.
  // If Kore's metadata changes in DB, update these three values.
  const DEFAULT_LANGUAGE_CODE = 'us-en';
  const DEFAULT_LANGUAGE_N8N_DESCR = 'American English';
  const DEFAULT_VOICE_ID = '12';
  const DEFAULT_VOICE_META = { gender: 'F', style: 'Firm', pitch: '7' };

  // Orgs
  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState(getSelectedOrgId());

  // Data sets
  const [interviews, setInterviews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);

  // Lookups
  const [languages, setLanguages] = useState([]);
  const [voices, setVoices] = useState([]);
  const [exitCodes, setExitCodes] = useState([]); // raw array from backend

  // Page filters (legacy)
  const [filters, setFilters] = useState({ job: "", candidate: "", status: "" });

  // Sort
  const [sort, setSort] = useState({ key: "scheduled", dir: "desc" });
  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "id" ? "desc" : "asc" }
    );
  };
  const sortArrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  // Excel-like filters
  const [excelFilters, setExcelFilters] = useState({}); // { key -> Set([...]) }
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const headerAnchorRefs = useRef({});

  // Form state
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    organization_id: null,
    candidate_id: "",
    job_req_id: "",
    interview_language_code: "us-en",
    interview_language_n8n_descr: "American English",
    interview_voice_id: "12",
    interview_no_questions: 5,
    interview_max_t: 30,
    answer_max_t: 180,
    interview_room_id: "",
    interview_scheduled_time: "",
    interview_status: 1,
    interview_start_time_display: "",
    interview_end_time_display: "",
    interview_exit_code: null,
    interview_exit_description: "",
  });

  // ---------------- Voice Persona Picker (Gender → Style → Pitch → Persona) ----------------
  const [voiceGender, setVoiceGender] = useState(DEFAULT_VOICE_META.gender);
  const [voiceStyle, setVoiceStyle] = useState(DEFAULT_VOICE_META.style);
  const [voicePitch, setVoicePitch] = useState(DEFAULT_VOICE_META.pitch);

  const [genderOptions, setGenderOptions] = useState([]);
  const [styleOptions, setStyleOptions] = useState([]);
  const [pitchOptions, setPitchOptions] = useState([]);
  const [personaOptions, setPersonaOptions] = useState([]);

  const audioRef = useRef(null);
  const isHydratingVoiceRef = useRef(false);
  const hydrateTargetVoiceIdRef = useRef(null);

	const hydrateVoiceSelectorsFromVoiceId = useCallback(
		async (voiceId, meta = {}) => {
			const idStr = voiceId == null ? '' : String(voiceId);
			const gender = meta.gender ?? meta.interview_voice_gender ?? DEFAULT_VOICE_META.gender ?? '';
			const style = meta.style ?? meta.interview_voice_style ?? DEFAULT_VOICE_META.style ?? '';
			const pitch = meta.pitch ?? meta.interview_voice_pitch ?? DEFAULT_VOICE_META.pitch ?? '';

			isHydratingVoiceRef.current = true;
			hydrateTargetVoiceIdRef.current = idStr || null;

			// Set gender first
			setVoiceGender(gender ? String(gender) : '');

			// If we have all three values, we need to load the cascading options
			if (gender && style && pitch) {
				try {
					// Load styles for this gender
					const styleRows = await apiFetch(`/api/interviews/voices/styles?gender=${encodeURIComponent(gender)}`);
					setStyleOptions(Array.isArray(styleRows) ? styleRows : []);
					setVoiceStyle(String(style));

					// Load pitches for this gender+style
					const pitchRows = await apiFetch(
						`/api/interviews/voices/pitches?gender=${encodeURIComponent(gender)}&style=${encodeURIComponent(style)}`
					);
					setPitchOptions(Array.isArray(pitchRows) ? pitchRows : []);
					setVoicePitch(String(pitch));

					// Load personas for this gender+style+pitch
					const personaRows = await apiFetch(
						`/api/interviews/voices/filtered?gender=${encodeURIComponent(gender)}&style=${encodeURIComponent(style)}&pitch=${encodeURIComponent(pitch)}`
					);
					setPersonaOptions(Array.isArray(personaRows) ? personaRows : []);

					// Set the voice id in form
					if (idStr) {
						setForm((prev) => ({ ...prev, interview_voice_id: idStr }));
					}
				} catch (e) {
					console.error('Failed to hydrate voice selectors:', e);
				}
			}

			isHydratingVoiceRef.current = false;
			hydrateTargetVoiceIdRef.current = null;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setForm]
	);

  // Determine if the selected Interview Language label contains Arabic script characters
  const isArabicInterviewLanguage = useMemo(() => {
    const code = form.interview_language_code;
    if (!code) return false;
    const rec = (languages || []).find((l) => (l.code ?? l.interview_language_code) === code);
    const label = (rec?.descr ?? rec?.interview_language_descr ?? '').toString();
    return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(label);
  }, [form.interview_language_code, languages]);

  // Load genders once
  useEffect(() => {
    (async () => {
      try {
        const rows = await apiFetch('/api/interviews/voices/genders');
        setGenderOptions(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error('Failed to load voice genders:', e);
        setGenderOptions([]);
      }
    })();
  }, []);

  // On initial load (when genderOptions are loaded), hydrate the default voice selectors
  const initialVoiceHydratedRef = useRef(false);
  useEffect(() => {
    if (initialVoiceHydratedRef.current) return;
    if (genderOptions.length === 0) return;
    // Only hydrate defaults if not editing (editingId is null)
    if (editingId !== null) return;
    
    initialVoiceHydratedRef.current = true;
    hydrateVoiceSelectorsFromVoiceId(DEFAULT_VOICE_ID, DEFAULT_VOICE_META);
  }, [genderOptions, editingId, hydrateVoiceSelectorsFromVoiceId]);

  // When Gender changes: reset downstream + fetch styles (only if NOT hydrating)
  useEffect(() => {
    // If hydrating, don't reset - the hydrate function handles everything
    if (isHydratingVoiceRef.current) return;
    
    setVoiceStyle('');
    setVoicePitch('');
    setStyleOptions([]);
    setPitchOptions([]);
    setPersonaOptions([]);
    setForm((prev) => ({ ...prev, interview_voice_id: '' }));

    if (!voiceGender) return;
    (async () => {
      try {
        const rows = await apiFetch(`/api/interviews/voices/styles?gender=${encodeURIComponent(voiceGender)}`);
        setStyleOptions(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error('Failed to load voice styles:', e);
        setStyleOptions([]);
      }
    })();
  }, [voiceGender]);

  // When Style changes: reset downstream + fetch pitches (only if NOT hydrating)
  useEffect(() => {
    // If hydrating, don't reset - the hydrate function handles everything
    if (isHydratingVoiceRef.current) return;
    
    setVoicePitch('');
    setPitchOptions([]);
    setPersonaOptions([]);
    setForm((prev) => ({ ...prev, interview_voice_id: '' }));

    if (!voiceGender || !voiceStyle) return;
    (async () => {
      try {
        const rows = await apiFetch(
          `/api/interviews/voices/pitches?gender=${encodeURIComponent(voiceGender)}&style=${encodeURIComponent(voiceStyle)}`
        );
        setPitchOptions(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error('Failed to load voice pitches:', e);
        setPitchOptions([]);
      }
    })();
  }, [voiceGender, voiceStyle]);

  // When Pitch changes: fetch personas (only if NOT hydrating)
  useEffect(() => {
    // If hydrating, don't reset - the hydrate function handles everything
    if (isHydratingVoiceRef.current) return;
    
    setPersonaOptions([]);
    setForm((prev) => ({ ...prev, interview_voice_id: '' }));

    if (!voiceGender || !voiceStyle || !voicePitch) return;
    (async () => {
      try {
        const rows = await apiFetch(
          `/api/interviews/voices/filtered?gender=${encodeURIComponent(voiceGender)}&style=${encodeURIComponent(voiceStyle)}&pitch=${encodeURIComponent(voicePitch)}`
        );
        const list = Array.isArray(rows) ? rows : [];
        setPersonaOptions(list);
      } catch (e) {
        console.error('Failed to load filtered voices:', e);
        setPersonaOptions([]);
      }
    })();
  }, [voiceGender, voiceStyle, voicePitch]);

  const playSelectedVoiceSample = useCallback(async () => {
		const selectedVoiceId = String(form.interview_voice_id || '');
		if (!selectedVoiceId) return;

		const selected = (personaOptions || []).find((v) => {
			const id = v.id ?? v.interview_voice_id;
			return String(id ?? '') === selectedVoiceId;
		});

    const sampleUrl = selected?.sample_url ?? selected?.interview_voice_sample_url;
    if (!sampleUrl) return;

		const src = `${process.env.PUBLIC_URL}/voice_samples/${sampleUrl}`;
    try {
      if (!audioRef.current) return;
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = src;
      await audioRef.current.play();
    } catch (e) {
      console.error('Failed to play sample audio:', e);
    }
  }, [form.interview_voice_id, personaOptions]);


  // Form options
  const [formCandidates, setFormCandidates] = useState([]);
  const [formJobs, setFormJobs] = useState([]);

  // Load orgs
  useEffect(() => {
    if (!isSuper) return;
    fetchOrganizations().then((d) => setOrgs(Array.isArray(d) ? d : [])).catch(() => setOrgs([]));
  }, [isSuper]);

  const orgNameById = useMemo(() => {
    const m = new Map();
    (orgs || []).forEach((o) => m.set(o.id ?? o.organization_id, o.name ?? o.organization_name));
    return m;
  }, [orgs]);

  // Resilient scoped fetch
  const fetchScopedList = useCallback(
    async (basePath, orgIdForScope) => {
      const superScope = isSuper;
      const useOrg = orgIdForScope;

      if (!superScope || useOrg == null) {
        if (!superScope) return (await apiFetch(basePath).catch(() => [])) ?? [];
        return (await apiFetch(`${basePath}?all=1`).catch(() => [])) ?? [];
      }

      let data = await apiFetch(`${basePath}?orgId=${useOrg}`).catch(() => null);
      if (Array.isArray(data)) return data;

      data = await apiFetch(`${basePath}?organizationId=${useOrg}`).catch(() => null);
      if (Array.isArray(data)) return data;

      data = await apiFetch(`${basePath}?organization_id=${useOrg}`).catch(() => null);
      if (Array.isArray(data)) return data;

      data = await apiFetch(`${basePath}?all=1`).catch(() => []);
      const orgKey = (row) =>
        row.organization_id ?? row.org_id ?? row.orgId ?? row.organizationId ?? null;
      return (Array.isArray(data) ? data : []).filter(
        (row) => Number(orgKey(row)) === Number(useOrg)
      );
    },
    [isSuper]
  );

  // Lookups (languages, voices, exit codes)
  useEffect(() => {
    Promise.all([
      apiFetch(`/api/interviews/languages`).catch(() => []),
      Promise.resolve([]), // removed legacy /api/interviews/voices call (was returning 400)
      apiFetch(`/api/interviews/exit-codes`).catch(() => []),
    ]).then(([langs, v, exits]) => {
      const langList = Array.isArray(langs) ? langs : [];
      setLanguages(langList);
      setVoices(Array.isArray(v) ? v : []);
      setExitCodes(Array.isArray(exits) ? exits : []);

      // Find the default language ("American English") and set its code
      // This ensures we use the correct code from the database
      const defaultLang = langList.find((l) => {
        const descr = (l.descr ?? l.interview_language_descr ?? '').toString();
        const n8n = (l.n8n_descr ?? l.interview_language_n8n_descr ?? '').toString();
        return descr === 'American English' || n8n === 'American English';
      });
      
      const defaultCode = defaultLang?.code ?? defaultLang?.interview_language_code ?? DEFAULT_LANGUAGE_CODE;
      const defaultN8n = defaultLang?.n8n_descr ?? defaultLang?.interview_language_n8n_descr ?? DEFAULT_LANGUAGE_N8N_DESCR;

      setForm((prev) => {
        const next = { ...prev };
        // Set default language if not already set to a valid code
        if (!next.interview_language_code || !langList.some(l => (l.code ?? l.interview_language_code) === next.interview_language_code)) {
          next.interview_language_code = defaultCode;
          next.interview_language_n8n_descr = defaultN8n;
        }
        return next;
      });
    });
  }, []);

  // Load lists per org change
  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      const orgIdForScope = isSuper ? orgFilter : null;
      try {
        const [ints, js, cs] = await Promise.all([
          fetchScopedList(`/api/interviews`, orgIdForScope),
          fetchScopedList(`/api/jobs`, orgIdForScope),
          fetchScopedList(`/api/candidates`, orgIdForScope),
        ]);
        if (cancelled) return;
        const filtered = (Array.isArray(ints) ? ints : []).filter(
          (r) => Number(r.interview_status ?? r.status) !== 0
        );
        setInterviews(filtered);
        setJobs(Array.isArray(js) ? js : []);
        setCandidates(Array.isArray(cs) ? cs : []);
      } catch (e) {
        if (!cancelled) {
          console.error("Interviews page load error:", e);
          setInterviews([]);
          setJobs([]);
          setCandidates([]);
        }
      }
    }
    setFilters({ job: "", candidate: "", status: "" });
    loadAll();
    return () => {
      cancelled = true;
    };
  }, [isSuper, orgFilter, fetchScopedList]);

  // Keep form options synced to form org
  useEffect(() => {
    const orgIdForForm = isSuper ? (form.organization_id ?? orgFilter) : null;
    const loadFormOptions = async () => {
      try {
        const [js, cs] = await Promise.all([
          fetchScopedList(`/api/jobs`, orgIdForForm),
          fetchScopedList(`/api/candidates`, orgIdForForm),
        ]);
        setFormJobs(Array.isArray(js) ? js : []);
        setFormCandidates(Array.isArray(cs) ? cs : []);
      } catch {
        setFormJobs([]);
        setFormCandidates([]);
      }
    };
    loadFormOptions();

    const st = location.state || {};
    const sp = new URLSearchParams(location.search);
    const preCreate = !!(st.create || sp.get("create") === "1");
    if (!preCreate) setForm((f) => ({ ...f, candidate_id: "", job_req_id: "" }));
  }, [form.organization_id, isSuper, orgFilter, fetchScopedList, location.state, location.search]);

  const handleOrgFilterChange = (e) => {
    const v = e.target.value;
    const next = v === "" ? null : Number(v);
    setOrgFilter(next);
    setSelectedOrgId(next);
  };

  // Display maps
  const candidateNameById = useMemo(() => {
    const m = new Map();
    (candidates || []).forEach((c) =>
      m.set(c.id ?? c.candidate_id, c.name ?? c.candidate_name)
    );
    return m;
  }, [candidates]);

  const jobTitleById = useMemo(() => {
    const m = new Map();
    (jobs || []).forEach((j) => m.set(j.id ?? j.job_req_id, j.title ?? j.job_req_title));
    return m;
  }, [jobs]);

  const langDescrByCode = useMemo(() => {
    const m = new Map();
    (languages || []).forEach((l) =>
      m.set(l.code ?? l.interview_language_code, l.descr ?? l.interview_language_descr)
    );
    return m;
  }, [languages]);

  const voicePersonaById = useMemo(() => {
    const m = new Map();
    (voices || []).forEach((v) =>
      m.set(v.id ?? v.interview_voice_id, v.persona ?? v.interview_voice_persona)
    );
    return m;
  }, [voices]);

  // Exit code → description map (robust to number/string/#number)
  // 🔧 Also harvest any description already present on interview rows (server-joined),
  // so tooltip still works even if the lookup endpoint is empty.
  const exitDescrByCode = useMemo(() => {
    const m = new Map();

    // From lookup endpoint
    for (const x of exitCodes || []) {
      const rawCode =
        x.code ??
        x.interview_exit_code ??
        x.id ??
        x.exit_code ??
        x.EXIT_CODE ??
        null;
      if (rawCode == null) continue;
      const code = normalizeExitCode(rawCode);
      const descr =
        x.description ??
        x.interview_exit_description ??
        x.descr ??
        x.name ??
        x.exit_description ??
        "";
      if (code == null || !descr) continue;
      m.set(code, descr);
      m.set(String(code), descr);
    }

    // From interviews (fallback / reinforcement)
    for (const r of interviews || []) {
      const code = normalizeExitCode(r.interview_exit_code ?? null);
      const descr =
        r.interview_exit_description ??
        r.exit_description ??
        r.exitStatusDescr ??
        "";
      if (code != null && descr) {
        m.set(code, descr);
        m.set(String(code), descr);
      }
    }
    return m;
  }, [exitCodes, interviews]);

  // Get status description using interviewStatusLabel helper
  const getStatusDescription = useCallback((statusNum) => {
    const num = Number(statusNum);
    // Use the existing interviewStatusLabel utility function
    return num === 9 ? "Pending Approval" : interviewStatusLabel(num);
  }, []);

  // Get exit description from exit code
  const getExitDescription = useCallback((exitCode) => {
    if (exitCode == null || exitCode === '') return '';
    const code = normalizeExitCode(exitCode);
    return exitDescrByCode.get(code) ?? exitDescrByCode.get(String(code)) ?? '';
  }, [exitDescrByCode]);

  /* --------- Derived list with filters + de-dup + sorting + Excel filters --------- */
  const baseRows = useMemo(() => {
    const map = new Map();
    (Array.isArray(interviews) ? interviews : []).forEach((r) => {
      const id = Number(r.interview_id ?? r.id);
      if (!map.has(id)) map.set(id, r);
      else {
        const prev = map.get(id);
        const prevT = new Date(prev.updated_at || prev.updatedAt || 0).valueOf();
        const curT = new Date(r.updated_at || r.updatedAt || 0).valueOf();
        if (curT >= prevT) map.set(id, r);
      }
    });
    return Array.from(map.values());
  }, [interviews]);

  const topFiltered = useMemo(() => {
    let rows = baseRows;
    rows = rows.filter((r) => Number(r.interview_status ?? r.status) !== 0);
    if (filters.job) rows = rows.filter((r) => (r.job_req_id ?? r.jobId) === Number(filters.job));
    if (filters.candidate)
      rows = rows.filter((r) => (r.candidate_id ?? r.candidateId) === Number(filters.candidate));
    if (filters.status !== "")
      rows = rows.filter(
        (r) => Number(r.interview_status ?? r.status) === Number(filters.status)
      );
    return rows;
  }, [baseRows, filters]);

  const colGetters = useMemo(
    () => ({
      id: (r) => Number(r.interview_id ?? r.id) || 0,
      candidate: (r) =>
        (candidateNameById.get(r.candidate_id) ??
          r.candidate_name ??
          String(r.candidate_id ?? "")).toString(),
      jobreq: (r) => Number(r.job_req_id ?? 0) || 0,
      title: (r) =>
        (jobTitleById.get(r.job_req_id) ?? r.job_req_title ?? r.title ?? "").toString(),
      language: (r) =>
        r.interview_language_descr ||
        langDescrByCode.get(r.interview_language_code) ||
        r.interview_language_code ||
        "",
      voice: (r) => {
        // Determine if language contains Arabic characters
        const lang = r.interview_language_descr ||
          langDescrByCode.get(r.interview_language_code) ||
          r.interview_language_code ||
          "";
        const isArabicLang = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(lang);
        
        if (isArabicLang) {
          return r.interview_voice_persona_name_ar || r.interview_voice_persona || voicePersonaById.get(r.interview_voice_id) || "";
        } else {
          return r.interview_voice_persona_name_en || r.interview_voice_persona || voicePersonaById.get(r.interview_voice_id) || "";
        }
      },
      questions: (r) => Number(r.interview_no_questions ?? r.no_questions ?? 0),
      answerMax: (r) => Number(r.answer_max_t ?? 0),
      interviewMax: (r) => Number(r.interview_max_t ?? r.max_t ?? 0),
      room: (r) => (r.interview_room_id ?? "").toString(),
      scheduled: (r) => {
        const s = r.interview_scheduled_time ?? r.scheduled_time;
        return s ? new Date(s).valueOf() : 0;
      },
      status: (r) => Number(r.interview_status ?? r.status ?? 0),
      org: (r) =>
        orgNameById.get(r.organization_id) ??
        (r.organization_id != null ? `#${r.organization_id}` : ""),
      // Header filters & sorting use description (not "#code")
      exitStatus: (r) => {
        const code = normalizeExitCode(r.interview_exit_code ?? null);
        if (code == null) return "";
        return exitDescrByCode.get(code) ?? exitDescrByCode.get(String(code)) ?? "";
      },
    }),
    [candidateNameById, jobTitleById, langDescrByCode, voicePersonaById, orgNameById, exitDescrByCode]
  );

  const getFilterValue = useCallback(
    (key, r) => {
      if (key === "scheduled") {
        const s = r.interview_scheduled_time ?? r.scheduled_time;
        return s ? toYmd(s) : ""; // filter by yyyy-mm-dd only
      }
      if (key === "status") {
        const n = Number(r.interview_status ?? r.status ?? 0);
        return n === 9 ? "Pending Approval" : interviewStatusLabel(n);
      }
      if (key === "exitStatus") {
        return colGetters.exitStatus(r); // description only
      }
      const getter = colGetters[key];
      const v = getter ? getter(r) : "";
      return typeof v === "number" ? v : (v ?? "").toString();
    },
    [colGetters]
  );

  const uniqueValuesForKey = useCallback(
    (key) => {
      const set = new Set();
      for (const r of topFiltered) set.add(getFilterValue(key, r));
      const arr = Array.from(set.values());
      return arr.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    },
    [topFiltered, getFilterValue]
  );

  const excelFiltered = useMemo(() => {
    const activeKeys = Object.keys(excelFilters).filter(
      (k) => excelFilters[k] && excelFilters[k] instanceof Set
    );
    if (activeKeys.length === 0) return topFiltered;
    return topFiltered.filter((r) => {
      for (const k of activeKeys) {
        const sel = excelFilters[k];
        if (!sel || sel.size === 0) return false;
        const fv = getFilterValue(k, r);
        if (!sel.has(fv)) return false;
      }
      return true;
    });
  }, [topFiltered, excelFilters, getFilterValue]);

  const sortedList = useMemo(() => {
    const rows = excelFiltered.slice(); // avoid duplicates on resort
    const getter = colGetters[sort.key] || (() => 0);
    rows.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [excelFiltered, sort, colGetters]);

  // Group by candidate
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of sortedList) {
      const name =
        candidateNameById.get(r.candidate_id) ??
        r.candidate_name ??
        `(Candidate #${r.candidate_id})`;
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(r);
    }
    return Array.from(map.entries());
  }, [sortedList, candidateNameById]);

  const startCreate = () => {
    setEditingId(null);
    // Reset the initial hydration flag so it can re-hydrate defaults
    initialVoiceHydratedRef.current = false;
    setForm({
      organization_id: isSuper ? (orgFilter ?? "") : null,
      candidate_id: "",
      job_req_id: "",
      interview_language_code: DEFAULT_LANGUAGE_CODE,
      interview_language_n8n_descr: DEFAULT_LANGUAGE_N8N_DESCR,
      interview_voice_id: DEFAULT_VOICE_ID,
      interview_no_questions: 5,
      interview_max_t: 30,
      answer_max_t: 180,
      interview_room_id: "",
      interview_scheduled_time: "",
      interview_status: 1,
      interview_start_time_display: "",
      interview_end_time_display: "",
      interview_exit_code: null,
      interview_exit_description: "",
    });

		// Default Voice selectors to Kore (id=12)
		hydrateVoiceSelectorsFromVoiceId(DEFAULT_VOICE_ID, DEFAULT_VOICE_META);
  };

  const startEdit = (row) => {
    setEditingId(row.interview_id ?? row.id);
		
		// Find the n8n_descr for the language
		const langCode = row.interview_language_code ?? DEFAULT_LANGUAGE_CODE;
		const langRec = (languages || []).find((l) => (l.code ?? l.interview_language_code) === langCode);
		const langN8nDescr = langRec?.n8n_descr ?? langRec?.interview_language_n8n_descr ?? row.interview_language_n8n_descr ?? DEFAULT_LANGUAGE_N8N_DESCR;
		
    setForm({
      organization_id: isSuper ? (row.organization_id ?? orgFilter ?? "") : null,
      candidate_id: row.candidate_id ?? "",
      job_req_id: row.job_req_id ?? "",
			interview_language_code: langCode,
			interview_language_n8n_descr: langN8nDescr,
      interview_voice_id: (row.interview_voice_id ?? "").toString(),
      interview_no_questions: Number(row.interview_no_questions ?? row.no_questions ?? 5),
      interview_max_t: Number(row.interview_max_t ?? row.max_t ?? 30),
      answer_max_t: Number(row.answer_max_t ?? 180),
      interview_room_id: row.interview_room_id ?? "",
      interview_scheduled_time: toDatetimeLocalValue(
        row.interview_scheduled_time ?? row.scheduled_time
      ),
      interview_status: Number(row.interview_status ?? row.status ?? 1),
      interview_start_time_display: fmtDateTime(row.interview_start_time),
      interview_end_time_display: fmtDateTime(row.interview_end_time),
      interview_exit_code: row.interview_exit_code ?? null,
      interview_exit_description: "",
    });

		// Hydrate voice selectors after setting form
		const voiceId = row.interview_voice_id ?? row.voice_id ?? row.voiceId;
		hydrateVoiceSelectorsFromVoiceId(voiceId, {
			gender: row.interview_voice_gender,
			style: row.interview_voice_style,
			pitch: row.interview_voice_pitch,
		});
  };

  const cancelEdit = () => startCreate();

  const buildSaveUrl = (id, effectiveOrgId) => {
    if (!isSuper) return id ? `/api/interviews/${id}` : `/api/interviews`;
    const qs =
      effectiveOrgId != null && effectiveOrgId !== ""
        ? `?orgId=${encodeURIComponent(effectiveOrgId)}`
        : orgFilter != null
        ? `?orgId=${encodeURIComponent(orgFilter)}`
        : "";
    return id ? `/api/interviews/${id}${qs}` : `/api/interviews${qs}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isCreate = !editingId;

    // ==================== REQUIRED FIELD VALIDATIONS ====================
    
    // Organization (for superadmin)
    if (isSuper && !form.organization_id) {
      return alert("Organization needs to be selected!");
    }

    // Candidate
    if (!form.candidate_id) {
      return alert("Candidate needs to be selected!");
    }

    // Job
    if (!form.job_req_id) {
      return alert("Job needs to be selected!");
    }

    // Interview Language
    if (!form.interview_language_code) {
      return alert("Interview Language needs to be selected!");
    }

    // Interview Voice (all 4 dropdowns)
    if (!voiceGender) {
      return alert("Interview Voice Gender needs to be selected!");
    }
    if (!voiceStyle) {
      return alert("Interview Voice Style needs to be selected!");
    }
    if (!voicePitch) {
      return alert("Interview Voice Pitch needs to be selected!");
    }
    if (!form.interview_voice_id) {
      return alert("Interview Voice Persona needs to be selected!");
    }

    // Number of Questions
    if (form.interview_no_questions === "" || form.interview_no_questions == null) {
      return alert("# Q's needs to be entered!");
    }

    // Interview Max Duration
    if (form.interview_max_t === "" || form.interview_max_t == null) {
      return alert("Interview Max Duration (minutes) needs to be entered!");
    }

    // Candidate Answer Max Duration
    if (form.answer_max_t === "" || form.answer_max_t == null) {
      return alert("Candidate Answer Max Duration (s) needs to be entered!");
    }

    // Scheduled Time
    if (!form.interview_scheduled_time) {
      return alert("Scheduled Time needs to be entered!");
    }

    // ==================== VALUE VALIDATIONS ====================

    const interview_no_questions = Number(form.interview_no_questions);
    const interview_max_t = Number(form.interview_max_t);
    const answer_max_t = Number(form.answer_max_t);

    // Validate # Q's >= 3
    if (interview_no_questions < 3) {
      return alert("The total number of interview questions are just too small to have a meaningful interview, please enter a value of 3 questions or more");
    }

    // Validate interview_max_t >= 10
    if (interview_max_t < 10) {
      return alert("The interview maximum duration is too short to assess the candidate, please enter a value of 10 minutes or more");
    }

    // Validate answer_max_t >= 10
    if (answer_max_t < 10) {
      return alert("The candidate's answer max duration time is just too short to get a meaningful response, please enter a value of 10 seconds or more");
    }

    // Validate interview_max_t >= 5 + (answer_max_t/60) * interview_no_questions
    const minRequiredTime = 5 + (answer_max_t / 60) * interview_no_questions;
    if (interview_max_t < minRequiredTime) {
      return alert("The interview time is too small to fit the required number of questions and their candidate answer time! Please enter enough Interview Max Duration time in minutes to accommodate the required number of questions and their allowed answer duration!");
    }

    // Warn if expected duration >= 120 minutes (2 hours)
    if (minRequiredTime >= 120) {
      const confirmed = window.confirm("The interview expected duration is about 2 hours or more, are you sure you want to proceed?");
      if (!confirmed) {
        return; // User cancelled
      }
    }

    // Validate Scheduled Time is in the future
    const scheduledDate = new Date(form.interview_scheduled_time);
    const now = new Date();
    if (scheduledDate <= now) {
      return alert("The entered Scheduled Time is a past date! Please enter a future interview time!");
    }

    // Validate the Meeting Room ID
    const interview_room_id_len = form.interview_room_id.length;
    if (interview_room_id_len < 6 || interview_room_id_len > 40) {
      return alert("Meeting Room ID must be 6–40 alphanumeric characters.");
    }
    
    
    // ==================== BUILD PAYLOAD ====================

    const effectiveOrgId = isSuper
      ? form.organization_id === ""
        ? null
        : Number(form.organization_id)
      : null;

    const payload = {};
    // Approve pending on update
    if (editingId && Number(form.interview_status) === 9) payload.interview_status = 1;
    else payload.interview_status = Number(form.interview_status);

    if (isCreate) {
      payload.candidate_id = Number(form.candidate_id);
      payload.job_req_id = Number(form.job_req_id);
      payload.interview_scheduled_time = form.interview_scheduled_time;
    } else {
      if (form.candidate_id) payload.candidate_id = Number(form.candidate_id);
      if (form.job_req_id) payload.job_req_id = Number(form.job_req_id);
      if (form.interview_scheduled_time)
        payload.interview_scheduled_time = form.interview_scheduled_time;
    }

    payload.interview_language_code = form.interview_language_code;
    if (form.interview_language_n8n_descr) payload.interview_language_n8n_descr = form.interview_language_n8n_descr;
    payload.interview_voice_id = Number(form.interview_voice_id);
    payload.interview_no_questions = interview_no_questions;
    payload.interview_max_t = interview_max_t;
    payload.answer_max_t = answer_max_t;
    const rid = onlyAlnum(form.interview_room_id || "");
    if (rid) payload.interview_room_id = rid;
    if (isSuper && effectiveOrgId != null) payload.organization_id = effectiveOrgId;

    if (form.interview_exit_code != null && form.interview_exit_code !== '')
      payload.interview_exit_code = normalizeExitCode(form.interview_exit_code);

    // ==================== SUBMIT ====================

    try {
      if (editingId) await apiPut(buildSaveUrl(editingId, effectiveOrgId), payload);
      else await apiPost(buildSaveUrl(null, effectiveOrgId), payload);

      const orgIdForScope = isSuper ? orgFilter : null;
      const [ints, js, cs] = await Promise.all([
        fetchScopedList(`/api/interviews`, orgIdForScope),
        fetchScopedList(`/api/jobs`, orgIdForScope),
        fetchScopedList(`/api/candidates`, orgIdForScope),
      ]);
      const visible = (Array.isArray(ints) ? ints : []).filter(
        (r) => Number(r.interview_status ?? r.status) !== 0
      );
      setInterviews(visible);
      setJobs(Array.isArray(js) ? js : []);
      setCandidates(Array.isArray(cs) ? cs : []);
      cancelEdit();
    } catch (err) {
      console.error("interview save failed:", err);
      alert(err?.message || "Failed to save interview");
    }
  };

  const cancelInterview = async (id) => {
    if (!id) return;
    const ok = window.confirm("Are you sure you want to cancel this interview?");
    if (!ok) return;
    try {
      await apiPut(`/api/interviews/${id}`, { interview_status: 0 });
      const orgIdForScope = isSuper ? orgFilter : null;
      const ints = await fetchScopedList(`/api/interviews`, orgIdForScope);
      const visible = (Array.isArray(ints) ? ints : []).filter(
        (r) => Number(r.interview_status ?? r.status) !== 0
      );
      setInterviews(visible);
    } catch (err) {
      console.error("Cancel interview failed:", err);
      alert("Failed to cancel interview.");
    }
  };

  // Preselect (compat)
  useEffect(() => {
    if (preselectApplied.current) return;
    const st = location.state || {};
    const sp = new URLSearchParams(location.search);
    const createFlag = !!(st.create || sp.get("create") === "1");
    if (!createFlag) return;
    const autoSelect = !!(st.autoSelect || sp.get("autoSelect") === "1");
    if (!autoSelect) return;

    const preCandidateId = String(
      st.candidateId ??
        st.candidate_id ??
        sp.get("candidateId") ??
        sp.get("candidate_id") ??
        ""
    );
    const preJobId = String(
      st.jobId ?? st.job_req_id ?? sp.get("jobId") ?? sp.get("job_req_id") ?? ""
    );
    const preOrgId =
      st.organization_id ?? st.orgId ?? sp.get("orgId") ?? sp.get("organization_id") ?? null;

    setEditingId(null);
    setForm((f) => ({
      ...f,
      organization_id:
        isSuper && preOrgId != null && preOrgId !== "" ? Number(preOrgId) : f.organization_id,
      candidate_id: preCandidateId || f.candidate_id,
      job_req_id: preJobId || f.job_req_id,
    }));
    preselectApplied.current = true;
  }, [location.state, location.search, isSuper]);

  const statusCellLabel = (num) => getStatusDescription(num);

  const headerCell = (label, key) => {
    if (!headerAnchorRefs.current[key]) headerAnchorRefs.current[key] = { current: null };
    const values = uniqueValuesForKey(key);
    const selected = excelFilters[key] ?? new Set(values);
    const active = selected.size !== values.length;
    return (
      <th
        className="p-2 border-b cursor-pointer select-none relative"
        onClick={() => toggleSort(key)}
      >
        <div className="flex items-center">
          <span>
            {label}
            {sortArrow(key)}
          </span>
          <span
            className="ml-2 px-1 rounded hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              setOpenFilterKey((prev) => (prev === key ? null : key));
            }}
            ref={(el) => (headerAnchorRefs.current[key] = { current: el })}
            title="Filter"
          >
            <FilterIcon active={active} />
          </span>
        </div>
        <FilterDropdown
          open={openFilterKey === key}
          onClose={() => setOpenFilterKey(null)}
          anchorRef={headerAnchorRefs.current[key]}
          values={values}
          selectedSet={selected}
          onChange={(setVal) =>
            setExcelFilters((prev) => ({ ...prev, [key]: setVal }))
          }
        />
      </th>
    );
  };

  const clearAllFilters = () => {
    setExcelFilters({});
    setFilters({ job: "", candidate: "", status: "" });
    setOpenFilterKey(null);
  };

  return (
    <div
      onClick={() => {
        if (openFilterKey) setOpenFilterKey(null);
      }}
    >
      <Navbar />
      <div className="p-8">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="text-2xl font-bold">Interviews</h1>
          <div className="ml-auto flex items-center gap-2">
            {isSuper && (
              <>
                <label className="text-sm text-gray-500">Organization:</label>
                <select
                  className="border p-2 rounded"
                  value={orgFilter ?? ""}
                  onChange={handleOrgFilterChange}
                >
                  <option value="">All</option>
                  {(orgs || []).map((o) => (
                    <option key={o.id ?? o.organization_id} value={o.id ?? o.organization_id}>
                      {o.name ?? o.organization_name}
                    </option>
                  ))}
                </select>
              </>
            )}
            <button
              className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
              onClick={startCreate}
            >
              + New Interview
            </button>
          </div>
        </div>

        {/* ---- FORM ---- */}
        <form onSubmit={handleSubmit} className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {isSuper && (
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Organization</label>
              <select
                className="border p-2 rounded"
                value={form.organization_id ?? ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    organization_id:
                      e.target.value === "" ? null : Number(e.target.value),
                  }))
                }
              >
                <option value="">Select Organization</option>
                {(orgs || []).map((o) => (
                  <option key={o.id ?? o.organization_id} value={o.id ?? o.organization_id}>
                    {o.name ?? o.organization_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Candidate</label>
            <select
              className="border p-2 rounded"
              value={form.candidate_id}
              onChange={(e) => setForm({ ...form, candidate_id: e.target.value })}
            >
              <option value="">Select Candidate</option>
              {(formCandidates || []).map((c) => (
                <option key={c.id ?? c.candidate_id} value={c.id ?? c.candidate_id}>
                  {c.name ?? c.candidate_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Job</label>
            <select
              className="border p-2 rounded"
              value={form.job_req_id}
              onChange={(e) => setForm({ ...form, job_req_id: e.target.value })}
            >
              <option value="">Select Job</option>
              {(formJobs || []).map((j) => (
                <option key={j.id ?? j.job_req_id} value={j.id ?? j.job_req_id}>
                  {j.title ?? j.job_req_title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Interview Language</label>
            <select
              className="border p-2 rounded"
              value={form.interview_language_code}
              onChange={(e) => {
                const selectedCode = e.target.value;
                const selectedLang = (languages || []).find((l) => (l.code ?? l.interview_language_code) === selectedCode);
                const n8nDescr = selectedLang?.n8n_descr ?? selectedLang?.interview_language_n8n_descr ?? '';
                setForm({ ...form, interview_language_code: selectedCode, interview_language_n8n_descr: n8nDescr });
              }}
            >
              <option value="">Select Interview Language</option>
              {(languages || []).map((l) => (
                <option
                  key={l.code ?? l.interview_language_code}
                  value={l.code ?? l.interview_language_code}
                >
                  {l.descr ?? l.interview_language_descr}
                </option>
              ))}
            </select>
          </div>



          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Interview Voice</label>

            {/* 4 dropdowns in the same overall width as the old single dropdown */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              {/* 1) Gender */}
              <select
                className="border p-2 rounded w-full"
                value={voiceGender}
                onChange={(e) => setVoiceGender(e.target.value)}
              >
                <option value="">Pick Gender</option>
                {(genderOptions || []).map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>

              {/* 2) Style */}
              <select
                className="border p-2 rounded w-full"
                value={voiceStyle}
                onChange={(e) => setVoiceStyle(e.target.value)}
                disabled={!voiceGender}
              >
                <option value="">Pick a Voice Style</option>
                {(styleOptions || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {/* 3) Pitch (value must remain numeric pitch) */}
              <select
                className="border p-2 rounded w-full"
                value={voicePitch}
                onChange={(e) => setVoicePitch(e.target.value)}
                disabled={!voiceGender || !voiceStyle}
              >
                <option value="">Pick a Voice Pitch</option>
                {(pitchOptions || []).map((p) => (
                  <option key={p.pitch} value={p.pitch}>
                    {p.label} ({p.pitch})
                  </option>
                ))}
              </select>

              {/* 4) Persona (final selection sets interview_voice_id in form) */}
              <div className="flex items-center gap-2 w-full">
                <select
                  className="border p-2 rounded w-full"
                  value={form.interview_voice_id || ''}
                  onChange={(e) => setForm({ ...form, interview_voice_id: e.target.value })}
                  disabled={!voiceGender || !voiceStyle || !voicePitch}
                >
                  <option value="">Choose an Interviewer</option>
                  {(personaOptions || []).map((v) => {
					    const id = v.id ?? v.interview_voice_id;
					    const persona = v.persona ?? v.interview_voice_persona;
					    const nameEn = v.persona_name_en ?? v.interview_voice_persona_name_en;
					    const nameAr = v.persona_name_ar ?? v.interview_voice_persona_name_ar;
					    const displayName = (isArabicInterviewLanguage ? nameAr : nameEn) || persona || '';
                    return (
					      <option key={id ?? persona} value={id ?? ''}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>

                <button
                  type="button"
                  className="border rounded px-3 py-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={playSelectedVoiceSample}
                  disabled={!form.interview_voice_id}
                  title="Play sample"
                  aria-label="Play voice sample"
                >
                  {/* small speaker icon (inline SVG) */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 10v4c0 1.1.9 2 2 2h3l4 3c.7.5 1.7 0 1.7-.9V5.9c0-.9-1-1.4-1.7-.9l-4 3H5c-1.1 0-2 .9-2 2z"/>
                    <path d="M16.5 12c0-1.8-1-3.4-2.5-4.2v8.4c1.5-.8 2.5-2.4 2.5-4.2z"/>
                    <path d="M14 3.2v2.2c2.9 1 5 3.8 5 6.6s-2.1 5.6-5 6.6v2.2c4.1-1.1 7-4.8 7-8.8s-2.9-7.7-7-8.8z"/>
                  </svg>
                </button>

                {/* hidden audio element */}
                <audio ref={audioRef} preload="none" />
              </div>
            </div>
          </div>








          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1"># Q's</label>
            <input
              type="number"
              className="border p-2 rounded"
              min={0}
              value={form.interview_no_questions}
              onChange={(e) =>
                setForm({ ...form, interview_no_questions: Number(e.target.value) || 0 })
              }
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Interview Max Duration (minutes)</label>
            <input
              type="number"
              className="border p-2 rounded"
              min={0}
              value={form.interview_max_t}
              onChange={(e) =>
                setForm({ ...form, interview_max_t: Number(e.target.value) || 0 })
              }
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Candidate Answer Max Duration (s)</label>
            <input
              type="number"
              className="border p-2 rounded"
              min={0}
              value={form.answer_max_t}
              onChange={(e) => setForm({ ...form, answer_max_t: Number(e.target.value) || 0 })}
            />
          </div>

          <div className="flex flex-col lg:col-span-2">
            <label className="text-xs text-gray-600 mb-1">Meeting Room ID</label>
            <div className="flex gap-2">
              <input
                className="border p-2 rounded flex-1"
                value={form.interview_room_id}
                onChange={(e) =>
                  setForm({ ...form, interview_room_id: onlyAlnum(e.target.value) })
                }
                placeholder="alphanumeric, 6–40 chars"
              />
              <button
                type="button"
                className="bg-gray-200 px-3 rounded"
                onClick={() => setForm((f) => ({ ...f, interview_room_id: genRoomId(16) }))}
              >
                Generate
              </button>
            </div>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Scheduled Time</label>
            <input
              type="datetime-local"
              className="border p-2 rounded"
              value={form.interview_scheduled_time}
              onChange={(e) => setForm({ ...form, interview_scheduled_time: e.target.value })}
            />
          </div>

          {/* Exit Status (read-only, shows description from DB) */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Exit Status</label>
            <input
              className="border p-2 rounded bg-gray-100 w-full"
              readOnly
              title="Interview Exit Status"
              value={getExitDescription(form.interview_exit_code) || ''}
            />
          </div>

          <div className="lg:col-span-3 flex flex-col gap-2">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-gray-600 mb-1 block">Status</label>
                <input
                  className="border p-2 rounded bg-gray-100 w-full"
                  value={statusCellLabel(Number(form.interview_status))}
                  readOnly
                  title="Status is display-only"
                />
              </div>
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-gray-600 mb-1 block">Start Time</label>
                <input
                  className="border p-2 rounded bg-gray-100 w-full"
                  value={form.interview_start_time_display}
                  readOnly
                />
              </div>
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-gray-600 mb-1 block">End Time</label>
                <input
                  className="border p-2 rounded bg-gray-100 w-full"
                  value={form.interview_end_time_display}
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 flex gap-2 mt-2">
            <button type="submit" className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700">
              {editingId
                ? Number(form.interview_status) === 9
                  ? "Update/Approve"
                  : "Update"
                : "Create"}
            </button>
            {editingId && (
              <button type="button" className="bg-gray-200 px-3 py-1 rounded" onClick={cancelEdit}>
                Cancel
              </button>
            )}
          </div>
        </form>
        {/* ---- END FORM ---- */}

        {/* ✅ Toolbar directly ABOVE the table header */}
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-500"></div>
          <button
            type="button"
            className="px-3 py-1 rounded text-sm font-semibold border border-blue-500 text-blue-600 hover:bg-blue-50"
            onClick={clearAllFilters}
            title="Reset all table filters"
          >
            Clear Filters
          </button>
        </div>

        <table className="w-full bg-white rounded shadow text-left">
          <thead className="text-sm">
            <tr>
              {headerCell("ID", "id")}
              {headerCell("Candidate", "candidate")}
              {headerCell("Job Req #", "jobreq")}
              {headerCell("Title", "title")}
              {headerCell("Language", "language")}
              {headerCell("Voice", "voice")}
              {headerCell("# Q's", "questions")}
              {headerCell("Answer Max\nSeconds", "answerMax")}
              {headerCell("Interview Max\nMinutes", "interviewMax")}
              {headerCell("Room ID", "room")}
              {headerCell("Scheduled", "scheduled")}
              {headerCell("Status", "status")}
              {isSuper && headerCell("Organization", "org")}
              {headerCell("Exit Status", "exitStatus")}
              <th className="p-2 border-b">Actions</th>
            </tr>
          </thead>

          <TableBodyGrouped
            groupedData={grouped}
            languages={languages}
            voices={voices}
            jobTitleById={jobTitleById}
            exitDescrByCode={exitDescrByCode}
            orgNameById={orgNameById}
            isSuper={isSuper}
            onEdit={startEdit}
            onCancel={cancelInterview}
          />
        </table>
      </div>
    </div>
  );
}

function TableBodyGrouped({
  groupedData,
  languages,
  voices,
  jobTitleById,
  exitDescrByCode,
  orgNameById,
  isSuper,
  onEdit,
  onCancel,
}) {
  // Helper to get status description
  const getStatusDescr = (statusNum) => {
    const num = Number(statusNum);
    return num === 9 ? "Pending Approval" : interviewStatusLabel(num);
  };

  return (
    <tbody className="text-[13px]">
      {groupedData.length === 0 && (
        <tr>
          <td className="p-3 text-gray-500" colSpan={100}>
            No interviews
          </td>
        </tr>
      )}

      {groupedData.map(([candName, rows]) => (
        <FragmentGroup key={candName} candidateName={candName}>
          {rows.map((r) => {
            const id = r.interview_id ?? r.id;
            const title =
              jobTitleById.get(r.job_req_id) ?? r.job_req_title ?? r.title ?? "-";
            const lang =
              r.interview_language_descr ||
              (languages.find(
                (l) =>
                  (l.code ?? l.interview_language_code) ===
                  r.interview_language_code
              )?.descr) ||
              r.interview_language_code ||
              "-";
            
            // Determine if language contains Arabic characters
            const isArabicLang = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(lang);
            
            // Display persona name based on language
            const voicePersona = (() => {
              if (isArabicLang) {
                return r.interview_voice_persona_name_ar || r.interview_voice_persona || "-";
              } else {
                return r.interview_voice_persona_name_en || r.interview_voice_persona || "-";
              }
            })();
            const qn = r.interview_no_questions ?? r.no_questions ?? "-";
            const ans = r.answer_max_t ?? "-";
            const mt = r.interview_max_t ?? r.max_t ?? "-";
            const rid = r.interview_room_id ?? "-";
            const sched = r.interview_scheduled_time ?? r.scheduled_time;
            const status = Number(r.interview_status ?? r.status);
            const reportId = r.interview_report_id;
            const clickable = status === 2 && reportId;

            // Use status description
            const statusDescr = getStatusDescr(status);

            const statusTitle =
              status === 2
                ? [
                    `Start: ${fmtDateTime(r.interview_start_time) || "-"}`,
                    `End: ${fmtDateTime(r.interview_end_time) || "-"}`,
                  ].join("\n")
                : "";

            // Exit tooltip (normalize code and map to description)
            const exitCodeNorm = normalizeExitCode(r.interview_exit_code ?? null);
            const exitTip =
              exitCodeNorm == null
                ? "Not set"
                : (exitDescrByCode.get(exitCodeNorm) ??
                   exitDescrByCode.get(String(exitCodeNorm)) ??
                   "Not set");

            // Row background color: status=2 -> yellow, status=3 -> light grey
            const rowBgColor = status === 2 ? "#fff2cc" : status === 3 ? "#e5e5e5" : undefined;
            // Text color: status=3 -> dark grey
            const rowTextColor = status === 3 ? "#6b7280" : undefined;

            return (
              <tr
                key={id}
                className={`hover:bg-gray-50 ${clickable ? "cursor-pointer" : ""}`}
                style={{ backgroundColor: rowBgColor, color: rowTextColor }}
                onClick={
                  clickable
                    ? () =>
                        window.open(
                          `${window.location.origin}/candidate-report/${reportId}`,
                          "_blank",
                          "noopener"
                        )
                    : undefined
                }
              >
                <td className="p-2 border-b">{id}</td>
                <td className="p-2 border-b">{candName}</td>
                <td className="p-2 border-b">{r.job_req_id ?? "-"}</td>
                <td className="p-2 border-b">{title}</td>
                <td className="p-2 border-b">{lang}</td>
                <td className="p-2 border-b">{voicePersona}</td>
                <td className="p-2 border-b">{qn}</td>
                <td className="p-2 border-b">{ans}</td>
                <td className="p-2 border-b">{mt}</td>
                <td className="p-2 border-b">{rid}</td>
                <td className="p-2 border-b">
                  {r.interview_scheduled_time_display ||
                    (sched ? new Date(sched).toLocaleString() : "")}
                </td>
                <td className="p-2 border-b" title={statusTitle}>
                  {statusDescr}
                </td>
                {isSuper && (
                  <td className="p-2 border-b">
                    {orgNameById.get(r.organization_id) ??
                      (r.organization_id != null ? `#${r.organization_id}` : "-")}
                  </td>
                )}
                {/* Exit Status: ONLY info icon; tooltip shows joined DESCRIPTION */}
                <td className="p-2 border-b">
                  <span
                    className="inline-block w-5 h-5 leading-5 text-center rounded-full border"
                    title={exitTip}
                    aria-label="Exit status"
                    style={{ fontSize: 12 }}
                  >
                    i
                  </span>
                </td>
                <td className="p-2 border-b" onClick={(e) => e.stopPropagation()}>
                  <button className="text-blue-700 mr-3" onClick={() => onEdit(r)}>
                    Edit
                  </button>
                  <button className="text-red-700" onClick={() => onCancel(id)}>
                    Cancel
                  </button>
                </td>
              </tr>
            );
          })}
        </FragmentGroup>
      ))}
    </tbody>
  );
}

function FragmentGroup({ candidateName, children }) {
  return (
    <>
      <tr>
        <td colSpan={100} className="bg-gray-100 text-gray-800 font-semibold sticky top-0 z-10 p-2">
          {candidateName}
        </td>
      </tr>
      {children}
    </>
  );
}


/* ---------------- Utilities (required helpers) ---------------- */

// Normalize exit code values coming from various shapes (number, string, "#3", etc.)
function normalizeExitCode(v) {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  if (!s) return null;
  const s2 = s.startsWith("#") ? s.slice(1) : s;
  const n = parseInt(s2, 10);
  if (!Number.isNaN(n) && String(n) === s2) return n;
  return s2;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

// Date -> YYYY-MM-DD (local)
function toYmd(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// Date/time string -> value for <input type="datetime-local"> (local) => YYYY-MM-DDTHH:mm
function toDatetimeLocalValue(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
}

// Friendly display: "YYYY-MM-DD HH:mm" (local)
function fmtDateTime(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
}

// Keep only alphanumeric characters (for Room ID validation/cleanup)
function onlyAlnum(s) {
  return String(s || "").replace(/[^a-zA-Z0-9]/g, "");
}

// Generate a random alphanumeric Room ID (16 chars by default)
function genRoomId(len = 16) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

/* ---------------- Excel-like filter UI helpers ---------------- */

function FilterIcon({ active }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={active ? "text-blue-600" : "text-gray-400"}
      aria-hidden="true"
    >
      <path d="M3 5h18v2l-7 8v4l-4 2v-6L3 7V5z" />
    </svg>
  );
}

function FilterDropdown({ open, onClose, anchorRef, values, selectedSet, onChange }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const anchorEl = anchorRef?.current;
    if (anchorEl) {
      const r = anchorEl.getBoundingClientRect();
      // Position the dropdown so its top-left corner is at the filter icon's bottom-left
      const top = r.bottom + window.scrollY + 2;
      const left = r.left + window.scrollX;
      setPos({ top, left });
    }

    const onDoc = (e) => {
      const p = panelRef.current;
      const a = anchorRef?.current;
      if (!p) return;
      if (p.contains(e.target)) return;
      if (a && a.contains(e.target)) return;
      onClose?.();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const list = Array.isArray(values) ? values : [];
  const selected = selectedSet instanceof Set ? selectedSet : new Set();

  const toggle = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange?.(next);
  };

  const clearAll = () => onChange?.(new Set());
  const selectAll = () => onChange?.(new Set(list));

  return (
    <div
      ref={panelRef}
      className="fixed z-50 bg-white border rounded shadow-md p-2 w-64"
      style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
    >
      <div className="flex items-center justify-between mb-2">
        <button type="button" className="text-xs text-blue-600 hover:underline" onClick={selectAll}>
          Select all
        </button>
        <button type="button" className="text-xs text-gray-600 hover:underline" onClick={clearAll}>
          Clear
        </button>
      </div>

      <div className="max-h-64 overflow-auto">
        {list.length === 0 ? (
          <div className="text-xs text-gray-500 p-2">No values</div>
        ) : (
          list.map((val) => (
            <label key={String(val)} className="flex items-center gap-2 text-sm px-1 py-1">
              <input
                type="checkbox"
                checked={selected.has(val)}
                onChange={() => toggle(val)}
              />
              <span className="truncate">{String(val)}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}